import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  Paper,
  IconButton,
  InputBase,
  Typography,
} from '@material-ui/core';
import { makeStyles, createStyles, Theme, alpha, useTheme } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SearchIcon from '@material-ui/icons/Search';

import Stack from 'components/shared/Stack';
import CopyToClipboard from 'components/shared/CopyToClipboard';
import HighlightedText from 'components/shared/HighlightedText';
import { ReactComponent as CopyIcon } from 'assets/images/content_copy.svg';

import { debounce } from 'utils/functions';
import { FaqEntry } from 'typings';

interface FaqProps {
  customerName?: string | null;
  entries: FaqEntry[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paperRoot: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: theme.palette.background.default,
      padding: theme.spacing(4, 8),
      borderRadius: theme.spacing(1),
      textAlign: 'left',
    },
    baseTypography: {
      fontWeight: 600,
    },
    faqTypography: {
      marginRight: theme.spacing(2),
    },
    accordionSummary: {
      borderLeft: `0.75rem ${theme.palette.primary.main} solid`,
      fontSize: 'var(--s2)',
    },
    accordionDetails: {
      background: theme.palette.background.default,
      padding: theme.spacing(1, 0, 2, 2),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
    },
    answerTypography: {
      whiteSpace: 'pre-wrap',
    },
    search: {
      position: 'relative',
      background: theme.palette.background.paper,
      borderRadius: theme.spacing(4),
      '&:hover': {
        background: alpha(theme.palette.background.paper, 0.25),
      },
      marginRight: theme.spacing(2),
      marginLeft: 0,
      width: '50%',
    },
    searchIcon: {
      padding: theme.spacing(0, 2),
      height: '100%',
      position: 'absolute',
      pointerEvents: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    inputRoot: {
      color: 'inherit',
      width: '100%',
    },
    inputInput: {
      padding: theme.spacing(1.5, 1.5, 1.5, 0),
      // vertical padding + font size from searchIcon
      paddingLeft: `calc(1em + ${theme.spacing(4)})`,
      transition: theme.transitions.create('width'),
      width: '100%',
    },
    btnWrapper: {
      marginTop: theme.spacing(2),
    },
    btnActions: {
      textTransform: 'none',
    },
  })
);

const FAQs = ({ entries, customerName }: FaqProps) => {
  // flag to let us know whether all accordions are in expanded state
  const [isExpandAll, setIsExpandAll] = useState<boolean | null>(null);
  const [searchResult, setSearchResult] = useState({ searchText: '', firstMatch: '' });
  const [clickedPanel, setClickedPanel] = useState('');
  const accordionPanels = useRef(new Set<string>());
  const elementFocusRef = useRef<Record<string, HTMLDivElement>>({});
  const classes = useStyles();
  const theme = useTheme();

  useEffect(() => {
    if (searchResult.firstMatch) {
      elementFocusRef.current[searchResult.firstMatch].scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'end',
      });
    }
  }, [searchResult.firstMatch]);

  const search = (searchText: string) => {
    const text = searchText.toLocaleLowerCase();
    let firstPanelMatch = '';

    if (searchText) {
      const panels = entries
        .map((entry, idx) =>
          entry.answer.toLocaleLowerCase().includes(text) ? `panel-${idx}` : ''
        )
        .filter(Boolean);

      panels.forEach((panel) => {
        if (panel && !accordionPanels.current.has(panel)) {
          accordionPanels.current.add(panel);
        }
      });

      setIsExpandAll(accordionPanels.current.size === entries.length);

      if (panels.length) {
        firstPanelMatch = panels[0];
      }
    }

    setSearchResult({ searchText: text, firstMatch: firstPanelMatch });
  };

  const searchHandler = useCallback(debounce(search, 500), []);

  const handleSearchInputTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchHandler(event.target.value);
  };

  const handlePanelClick = (panel: string) => (event: any, shouldExpand: boolean) => {
    if (shouldExpand) {
      accordionPanels.current.add(panel);
    } else {
      accordionPanels.current.delete(panel);
    }

    setClickedPanel(clickedPanel !== panel ? panel : '');
    if (shouldExpand) {
      setIsExpandAll(false);
    }
  };

  const toggleExpandAccordionAll = (shouldExpand: boolean) => {
    return () => {
      if (shouldExpand) {
        if (!isExpandAll) {
          const panels = new Array(entries.length).fill(null).map((_, idx) => `panel-${idx}`);
          accordionPanels.current = new Set([...panels]);
        }
        setIsExpandAll(true);
      } else {
        if (accordionPanels.current.size) {
          accordionPanels.current.clear();
          setIsExpandAll(isExpandAll ? false : null);
        }
      }
    };
  };

  if (!entries.length) {
    return null;
  }

  return (
    <Paper variant="outlined" className={classes.paperRoot}>
      <Stack space="var(--s3)">
        <Box>
          <Typography
            variant="h5"
            color="primary"
            align="center"
            className={classes.baseTypography}
          >
            Frequently Asked Questions
          </Typography>
          {customerName && (
            <Typography variant="body1" color="primary" align="center">
              {customerName}
            </Typography>
          )}
        </Box>
        <Box className={classes.search}>
          <div className={classes.searchIcon}>
            <SearchIcon />
          </div>
          <InputBase
            placeholder="Search…"
            classes={{
              root: classes.inputRoot,
              input: classes.inputInput,
            }}
            inputProps={{ 'aria-label': 'search' }}
            onChange={handleSearchInputTextChange}
          />
        </Box>
      </Stack>
      <Box className={classes.btnWrapper}>
        <Button
          size="small"
          color="primary"
          className={classes.btnActions}
          onClick={toggleExpandAccordionAll(true)}
        >
          Expand All
        </Button>
        <Button
          size="small"
          color="primary"
          className={classes.btnActions}
          onClick={toggleExpandAccordionAll(false)}
        >
          Collapse All
        </Button>
      </Box>
      <Stack space="var(--s1)">
        {entries.map((entry, pos) => (
          <Accordion
            key={entry.uuid}
            elevation={0}
            expanded={accordionPanels.current.has(`panel-${pos}`)}
            onChange={handlePanelClick(`panel-${pos}`)}
          >
            <AccordionSummary
              className={classes.accordionSummary}
              expandIcon={<ExpandMoreIcon color="primary" />}
              aria-controls={`panel-${pos}-content`}
              id={`panel-${pos}-header`}
            >
              <Typography
                variant="body1"
                color="primary"
                display="inline"
                classes={{ root: classes.faqTypography }}
              >
                {`Q${pos + 1}`}
              </Typography>
              <Typography variant="body1" display="inline">
                <HighlightedText text={entry.question} searchText={searchResult.searchText} />
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              className={classes.accordionDetails}
              ref={(el: HTMLDivElement) => (elementFocusRef.current[`panel-${pos}`] = el)}
            >
              <Typography className={classes.answerTypography}>
                <HighlightedText text={entry.answer} searchText={searchResult.searchText} />
              </Typography>
              <CopyToClipboard text={entry.answer}>
                <IconButton color="secondary">
                  <CopyIcon fill={theme.palette.secondary.main} />
                </IconButton>
              </CopyToClipboard>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
    </Paper>
  );
};

export default FAQs;
